import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Loader2 } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1590301157172-7ba48dd1c2b2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxhcnRpc2FuJTIwc291cmRvdWdoJTIwYnJlYWQlMjBydXN0aWN8ZW58MHx8fHwxNzg3NTg0MDE1fDA&ixlib=rb-4.1.0&q=85";

export function AuthShell({ children, title, subtitle }) {
  return (
    <div className="flex min-h-screen bg-cream">
      <div className="relative hidden w-1/2 lg:block">
        <img src={HERO} alt="Bakery" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-cocoa/30" />
        <div className="absolute left-10 top-10"><Logo light /></div>
        <div className="absolute bottom-12 left-10 max-w-sm">
          <p className="font-serif text-3xl leading-tight text-warmwhite">Your bakery, beautifully organized.</p>
          <p className="mt-2 text-sm text-warmwhite/80">Preorders, pickup windows, recipes, and bake days — all in one calm place.</p>
        </div>
      </div>
      <div className="flex w-full items-center justify-center px-5 py-12 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden"><Logo /></div>
          <h1 className="font-serif text-3xl font-semibold tracking-tight text-cocoa">{title}</h1>
          <p className="mt-2 text-sm text-cocoa/60">{subtitle}</p>
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data?.user?.role === "admin") navigate("/admin");
      else if (data?.bakery) navigate(location.state?.from || "/dashboard");
      else navigate("/onboarding");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to your bakery dashboard.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <Label htmlFor="email" className="text-cocoa">Email</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 rounded-xl bg-warmwhite" data-testid="login-email" />
        </div>
        <div>
          <Label htmlFor="password" className="text-cocoa">Password</Label>
          <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5 rounded-xl bg-warmwhite" data-testid="login-password" />
        </div>
        <div className="text-right">
          <Link to="/forgot-password" className="text-sm font-medium text-terracotta hover:underline" data-testid="login-forgot-link">Forgot password?</Link>
        </div>
        {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
        <Button type="submit" disabled={loading} className="w-full rounded-full bg-terracotta py-6 text-warmwhite hover:bg-terracotta/90" data-testid="login-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
        </Button>
      </form>
      <p className="mt-6 text-center text-sm text-cocoa/60">
        New here? <Link to="/register" className="font-medium text-terracotta hover:underline" data-testid="login-to-register">Start your bakery</Link>
      </p>
    </AuthShell>
  );
}
